import type { NpsAggregateResult } from '@/types';

interface NpsInsightsPanelProps {
  aggregate: NpsAggregateResult;
}

function getInsights(aggregate: NpsAggregateResult): { strengths: string[]; improvements: string[]; tips: string[] } {
  const { score, promoters, passives, detractors, total } = aggregate;
  const strengths: string[] = [];
  const improvements: string[] = [];
  const tips: string[] = [];

  if (score >= 50) {
    strengths.push('Excellent NPS score — employers value your profile');
    strengths.push(`${promoters} promoters recommend you`);
  } else if (score >= 0) {
    strengths.push('Positive NPS — room to grow');
    if (promoters > passives) strengths.push('More promoters than passives');
  } else {
    improvements.push('NPS score is negative — focus on profile improvements');
  }

  if (detractors > 0) {
    improvements.push(`${detractors} detractor${detractors !== 1 ? 's' : ''} — review feedback for common themes`);
  }

  if (passives > promoters && total > 5) {
    tips.push('Many passives — small improvements could convert them to promoters');
  }

  if (total < 5) {
    tips.push('Share your profile with more employers to get meaningful feedback');
  }

  tips.push('Keep your skills and experience up to date');
  tips.push('Respond promptly to employer inquiries');

  return { strengths, improvements, tips };
}

export function NpsInsightsPanel({ aggregate }: NpsInsightsPanelProps) {
  const { strengths, improvements, tips } = getInsights(aggregate);

  return (
    <div style={{
      padding: '12px 16px', borderRadius: 12,
      background: 'var(--bp-surface-raised, #1a2332)',
      border: '1px solid rgba(255,255,255,0.06)',
    }}>
      <h3 style={{ margin: '0 0 12px', fontSize: '0.9rem', fontWeight: 600 }}>Insights</h3>

      {strengths.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--bp-success, #22c55e)', marginBottom: 4 }}>
            Your strengths
          </div>
          {strengths.map((s, i) => (
            <div key={i} style={{ fontSize: '0.8rem', color: 'var(--bp-text-secondary, #94a3b8)', marginBottom: 2 }}>
              ✓ {s}
            </div>
          ))}
        </div>
      )}

      {improvements.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--bp-warning, #f59e0b)', marginBottom: 4 }}>
            Areas for improvement
          </div>
          {improvements.map((s, i) => (
            <div key={i} style={{ fontSize: '0.8rem', color: 'var(--bp-text-secondary, #94a3b8)', marginBottom: 2 }}>
              ○ {s}
            </div>
          ))}
        </div>
      )}

      <div>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--bp-blue-400, #60a5fa)', marginBottom: 4 }}>
          Tips to increase your NPS
        </div>
        {tips.map((t, i) => (
          <div key={i} style={{ fontSize: '0.8rem', color: 'var(--bp-text-secondary, #94a3b8)', marginBottom: 2 }}>
            → {t}
          </div>
        ))}
      </div>
    </div>
  );
}
